import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, StyleSheet, Text, View } from 'react-native';
import { imageSource, JellyfinItem } from '../api/jellyfin';
import { Credentials } from '../storage/credentials';
import { motion, systemSansFont } from '../theme/tokens';
import { CardHoverOverlay } from './CardHoverOverlay';
import { FocusablePressable } from './FocusablePressable';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

type Props = {
    item: JellyfinItem;
    credentials: Credentials;
    width: number;
    television?: boolean;
    showTitle?: boolean;
    onInfo: () => void;
    onPlay: () => void;
};

export function SearchPosterCard({ item, credentials, width, television = false, showTitle = false, onInfo, onPlay }: Props) {
    const [active, setActive] = useState(false);
    const [failed, setFailed] = useState(false);
    const attention = useRef(new Animated.Value(0)).current;
    const height = Math.round(width * 1.5);
    const typeLabel = item.Type === 'Series' ? 'TV Show' : item.Type === 'Movie' ? 'Movie' : item.Type === 'TvChannel' ? 'Live TV' : item.Type;
    const hasArtwork = Boolean(item.ImageTags?.Primary);
    const imageUnavailable = failed || !hasArtwork;
    const titleGradientId = `search-title-${item.Id ?? 'poster'}`;

    useEffect(() => {
        Animated.timing(attention, {
            toValue: active ? 1 : 0,
            duration: active ? motion.controlEnter : motion.controlExit,
            easing: active ? Easing.out(Easing.cubic) : Easing.inOut(Easing.quad),
            useNativeDriver: true,
        }).start();
    }, [active, attention]);

    return (
        <Animated.View style={[styles.root, { width, transform: [
            { translateY: attention.interpolate({ inputRange: [0, 1], outputRange: [0, -4] }) },
            { scale: attention.interpolate({ inputRange: [0, 1], outputRange: [1, 1.025] }) },
        ] }]}> 
            <FocusablePressable
                testID={`search-poster-card-${item.Id ?? item.Name ?? 'title'}`}
                accessibilityLabel={`Open ${item.Name ?? 'title'}`}
                onFocus={() => setActive(true)}
                onBlur={() => setActive(false)}
                onHoverIn={() => setActive(true)}
                onHoverOut={() => setActive(false)}
                onPress={onInfo}
                style={(focused, pressed) => [styles.pressable, pressed && styles.pressed]}
            >
                <View style={[styles.imageShell, { width, height }, active && styles.imageShellActive]}>
                    {!imageUnavailable ? <Animated.Image
                        source={imageSource(credentials, item, 'Primary', Math.round(width * 2))}
                        onError={() => setFailed(true)}
                        resizeMode="cover"
                        style={{ width, height, opacity: attention.interpolate({ inputRange: [0, 1], outputRange: [1, 0.64] }), transform: [{ scale: attention.interpolate({ inputRange: [0, 1], outputRange: [1, 1.045] }) }] }}
                    /> : <View style={[styles.fallback, { width, height }]}><Text numberOfLines={4} style={[styles.fallbackText, television && styles.fallbackTextTelevision]}>{item.Name ?? 'Untitled'}</Text></View>}
                    <View pointerEvents="none" style={[styles.factRow, television && styles.factRowTelevision]}>
                        {typeLabel ? <View style={[styles.factChip, television && styles.factChipTelevision]}><Text style={[styles.factChipText, television && styles.factChipTextTelevision]}>{typeLabel}</Text></View> : null}
                        {item.ProductionYear ? <View style={[styles.factChip, television && styles.factChipTelevision]}><Text style={[styles.factChipText, television && styles.factChipTextTelevision]}>{item.ProductionYear}</Text></View> : null}
                    </View>
                    {showTitle && !imageUnavailable ? <>
                        <Svg pointerEvents="none" width={width} height={television ? 96 : 70} style={styles.titleShade}>
                            <Defs><LinearGradient id={titleGradientId} x1="0" y1="0" x2="0" y2="1"><Stop offset="0" stopColor="#080808" stopOpacity="0" /><Stop offset="0.42" stopColor="#080808" stopOpacity="0.58" /><Stop offset="1" stopColor="#080808" stopOpacity="0.95" /></LinearGradient></Defs>
                            <Rect width={width} height={television ? 96 : 70} fill={`url(#${titleGradientId})`} />
                        </Svg>
                        <View pointerEvents="none" style={[styles.titleOverlay, television && styles.titleOverlayTelevision]}><Text numberOfLines={2} style={[styles.titleOverlayText, television && styles.titleOverlayTextTelevision]}>{item.Name ?? 'Untitled'}</Text></View>
                    </> : null}
                    <CardHoverOverlay item={item} credentials={credentials} imageKind="Primary" visible={active} width={width} height={height} landscape standaloneInfo onPlay={onPlay} onInfo={onInfo} onHoverChange={setActive} />
                </View>
            </FocusablePressable>
        </Animated.View>
    );
}

const styles = StyleSheet.create({
    root: { flexShrink: 0, zIndex: 1 },
    pressable: { flexShrink: 0 },
    pressed: { opacity: 0.84 },
    imageShell: { position: 'relative', overflow: 'hidden', borderRadius: 9, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', backgroundColor: 'rgba(255,255,255,0.08)', shadowColor: '#000', shadowOpacity: 0.24, shadowRadius: 10, elevation: 3 },
    imageShellActive: { borderColor: 'rgba(255,255,255,0.66)', shadowOpacity: 0.54, shadowRadius: 18, elevation: 9 },
    fallback: { paddingHorizontal: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.12)' },
    fallbackText: { color: 'rgba(255,255,255,0.78)', textAlign: 'center', fontFamily: systemSansFont, fontSize: 18, lineHeight: 23, fontWeight: '800' },
    fallbackTextTelevision: { fontSize: 24, lineHeight: 30 },
    factRow: { position: 'absolute', top: 8, left: 8, zIndex: 4, flexDirection: 'row', gap: 4 },
    factRowTelevision: { top: 12, left: 12, gap: 6 },
    factChip: { paddingHorizontal: 6, paddingVertical: 3, borderRadius: 4, borderWidth: StyleSheet.hairlineWidth, borderColor: 'rgba(255,255,255,0.22)', backgroundColor: 'rgba(5,6,8,0.68)' },
    factChipTelevision: { paddingHorizontal: 9, paddingVertical: 5, borderRadius: 6, borderWidth: 1 },
    factChipText: { color: 'rgba(255,255,255,0.9)', fontFamily: systemSansFont, fontSize: 9, lineHeight: 11, fontWeight: '800' },
    factChipTextTelevision: { fontSize: 13, lineHeight: 16 },
    titleShade: { position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 2 },
    titleOverlay: { position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 2, minHeight: 58, paddingHorizontal: 10, paddingTop: 22, paddingBottom: 9, justifyContent: 'flex-end' },
    titleOverlayTelevision: { minHeight: 82, paddingHorizontal: 14, paddingTop: 30, paddingBottom: 13 },
    titleOverlayText: { color: '#fff', fontFamily: systemSansFont, fontSize: 13, lineHeight: 17, fontWeight: '800' },
    titleOverlayTextTelevision: { fontSize: 18, lineHeight: 23 },
});
